import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * `<InformedExportView />` — informed-export flow with optional
 * cryptographic-erasure composition per ADR-024 §2.7.
 *
 * Drives the `IExportFlow` orchestrator + dispatches step views by
 * current state. Composes:
 *
 *   IProjectionServiceClient       — export-bundle source +
 *                                    PHG-resident erasure invocation
 *                                    (per ADR-024 §2.7.3 step 5)
 *   HolderRetainedErasureFn        — substrate-side holder-retained
 *                                    pathway (per ADR-024 §2.6); app
 *                                    shell wires this; mocked in
 *                                    tests + dev
 *
 * **Doctrinal posture.**
 *   - Informed-export notice (state 'notice-presentation') is the
 *     load-bearing patient-fiduciary disclosure surface for export.
 *   - Erasure-composition selection (state 'erasure-composition-
 *     selection') has no pre-selected default per ADR-024 §2.7.2.
 *     The three-outcome model (`erase-both` / `erase-holder-only` /
 *     `erase-phg-only`) is committed at doctrine layer per Doctrine
 *     v1.3 §8 (Bundle B.2 ratified 2026-05-06): "Three valid outcomes
 *     per patient invocation."
 *   - Recovery-failure ≠ erasure per ADR-024 §2.7.5 + Doctrine v1.3
 *     §8 ("Recovery-failure scenarios … are explicitly NOT erasure");
 *     export's "leave the substrate" framing is distinct from
 *     recovery's "I lost access" framing.
 *
 * **Doctrinal-copy gate.** Patient-Facing Terms v1.3 (coord issue
 * #42) supplies canonical export + erasure language when v1.3
 * lands. Until then, step views ship placeholder copy aligned with
 * PFO §7.2 + ADR-024 §2.7 + Tech Spec §6.1.
 */
import * as React from 'react';
import { useLocale } from '../i18n/locale-context.js';
import { ExportIntroStep } from './_export/intro-step.js';
import { ExportScopeStep } from './_export/scope-step.js';
import { ExportFormatStep } from './_export/format-step.js';
import { ExportDestinationStep } from './_export/destination-step.js';
import { ExportNoticeStep } from './_export/notice-step.js';
import { ErasureCompositionStep } from './_export/erasure-composition-step.js';
import { ExportConfirmationStep } from './_export/confirmation-step.js';
import { ExportExecutingStep } from './_export/executing-step.js';
import { ExportCompleteStep } from './_export/complete-step.js';
import { ExportCancelledOrErrorStep } from './_export/cancelled-or-error-step.js';
function useExportFlowState(flow) {
    const [state, setState] = React.useState(() => flow.getState());
    React.useEffect(() => {
        return flow.subscribe(setState);
    }, [flow]);
    return state;
}
export function InformedExportView(props) {
    const state = useExportFlowState(props.flow);
    const { t } = useLocale();
    return (_jsxs("section", { "aria-labelledby": "export-heading", children: [_jsx("h2", { id: "export-heading", children: t('export.heading') }), renderStep(state, props)] }));
}
function renderStep(state, props) {
    const { flow } = props;
    const handleRestart = () => {
        if (props.onRestartRequested !== undefined) {
            props.onRestartRequested();
        }
    };
    switch (state.kind) {
        case 'intro':
            return (_jsx(ExportIntroStep, { onBegin: () => flow.beginScopeSelection(), onCancel: () => flow.cancel('user-cancelled-at-intro') }));
        case 'scope-selection':
            return (_jsx(ExportScopeStep, { onSelectCategories: (categories) => flow.selectCategories(categories), onCancel: () => flow.cancel('user-cancelled-at-scope') }));
        case 'format-selection':
            return (_jsx(ExportFormatStep, { onSelectFormat: (format) => flow.selectFormat(format), onCancel: () => flow.cancel('user-cancelled-at-format') }));
        case 'destination-selection':
            return (_jsx(ExportDestinationStep, { onSelectDestination: (destination) => flow.selectDestination(destination), onCancel: () => flow.cancel('user-cancelled-at-destination') }));
        case 'notice-presentation':
            return (_jsx(ExportNoticeStep, { selection: state.selection, onAcknowledge: () => flow.acknowledgeNotice(), onCancel: () => flow.cancel('user-cancelled-at-notice') }));
        case 'erasure-composition-selection':
            return (_jsx(ErasureCompositionStep, { onSelectComposition: (composition) => flow.selectErasureComposition(composition), onCancel: () => flow.cancel('user-cancelled-at-erasure-composition') }));
        case 'confirmation':
            return (_jsx(ExportConfirmationStep, { selection: state.selection, erasureComposition: state.erasureComposition, onConfirm: () => {
                    void flow.confirm();
                }, onCancel: () => flow.cancel('user-cancelled-at-confirmation') }));
        case 'executing':
            return _jsx(ExportExecutingStep, { erasureComposition: state.erasureComposition });
        case 'complete':
            return (_jsx(ExportCompleteStep, { result: state.result, erasureComposition: state.erasureComposition, ...(state.erasureAudit !== undefined ? { erasureAudit: state.erasureAudit } : {}) }));
        case 'cancelled':
            return (_jsx(ExportCancelledOrErrorStep, { kind: "cancelled", detail: state.reason, onRestart: handleRestart }));
        case 'error':
            return (_jsx(ExportCancelledOrErrorStep, { kind: "error", detail: `${state.code}: ${state.message}`, onRestart: handleRestart }));
    }
}
//# sourceMappingURL=informed-export-view.js.map